import * as React from 'react';
import { Country, State } from 'country-state-city';
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
} from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';

interface SubdivisionOption {
  value: string;
  label: string;
}

interface CountrySubdivisionInputProps {
  id?: string;
  countryCode: string | undefined;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function CountrySubdivisionInput({
  id,
  countryCode,
  value,
  onChange,
  onBlur,
  placeholder = 'Select…',
  disabled,
  className,
}: CountrySubdivisionInputProps) {
  const options = React.useMemo<SubdivisionOption[]>(() => {
    if (!countryCode) return [];
    const country = Country.getAllCountries().find((c) => c.name === countryCode);
    if (!country) return [];
    return State.getStatesOfCountry(country.isoCode).map((s) => ({
      value: s.name,
      label: s.name,
    }));
  }, [countryCode]);

  if (!countryCode) {
    return <Input id={id} placeholder="No country selected" disabled className={className} />;
  }

  if (options.length === 0) {
    return <Input id={id} placeholder="N/A" disabled className={className} />;
  }

  return (
    <Combobox value={value} onValueChange={(val) => onChange(val ?? '')} items={options}>
      <ComboboxInput
        id={id}
        placeholder={placeholder}
        onBlur={onBlur}
        disabled={disabled}
        showClear
        className={className}
      />
      <ComboboxContent>
        <ComboboxEmpty>No results found.</ComboboxEmpty>
        <ComboboxList>
          {(option: SubdivisionOption) => (
            <ComboboxItem key={option.value} value={option.value}>
              {option.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
